import { ReactElement } from 'react';

import { WaitlistEntry } from '@dreryk/egabinet-waitlist-frontend-api';
import { ServiceLocationType } from '@dreryk/egabinet-healthcare-provider-published-language-api';

import {
   ButtonVariant,
   Modal,
   ModalCancelButton,
   ModalCloseButton,
   ModalConfirmButton,
   ModalWidth,
   StyledFlex,
} from '@dreryk/react-components';

import { translationKeys } from '../translations';

import { AppointmentTimeSlotSection } from './components/AppointmentTimeSlotSection';
import { EncounterModeTextView } from './components/EncounterModeTextView';
import { LocationTextView } from './components/LocationTextView/LocationTextView';
import { NoteTextView } from './components/NoteTextView';
import { PatientTextView } from './components/PatientTextView';
import { PractitionerTextView } from './components/PractitionerTextView';
import { ReceptionServiceTextView } from './components/ReceptionServiceTextView';
import { UrgentLabel } from './components/UrgentLabel';
import { WaitlistEntryConfirmReservationModalActions } from './WaitlistEntryConfirmReservationModal.actions';
import { WaitlistEntryConfirmReservationModalLoader } from './WaitlistEntryConfirmReservationModal.loader';
import { WaitlistEntryConfirmReservationHealthcareLocationLoader } from './WaitlistEntryConfirmReservationHealthcareLocation.loader';

type WaitlistEntryConfirmReservationModalProps = {
   onClose: VoidFunction;
   waitlistEntry: WaitlistEntry;
};

// eslint-disable-next-line max-lines-per-function
export const WaitlistEntryConfirmReservationModal = (
   props: WaitlistEntryConfirmReservationModalProps
): ReactElement => {
   return (
      <Modal
         testId={'waitlistEntryConfirmReservation'}
         title={translationKeys.confirmAppointmentWithPatient}
         width={ModalWidth.WIDE}
      >
         <WaitlistEntryConfirmReservationModalLoader
            patientId={props.waitlistEntry.patient.id.asNumber()}
            appointmentId={props.waitlistEntry.appointmentId}
         >
            {({ appointmentDetails, patient }) => (
               <StyledFlex direction={'column'} gap={'24px'}>
                  {props.waitlistEntry.importance === 'IMPORTANT' ? <UrgentLabel /> : null}

                  {appointmentDetails.location ? (
                     <WaitlistEntryConfirmReservationHealthcareLocationLoader
                        healthcareLocationIdentifier={{
                           type: appointmentDetails.location.type as ServiceLocationType,
                           id: appointmentDetails.location.locationId,
                        }}
                     >
                        {({ locationPart }) => (
                           <LocationTextView
                              appointmentLocationIdentifier={appointmentDetails.location}
                              appointmentLocationPart={locationPart}
                              location={props.waitlistEntry.location}
                              reservationPreference={props.waitlistEntry.reservationPreference}
                           />
                        )}
                     </WaitlistEntryConfirmReservationHealthcareLocationLoader>
                  ) : (
                     <LocationTextView
                        appointmentLocationIdentifier={undefined}
                        appointmentLocationPart={undefined}
                        location={props.waitlistEntry.location}
                        reservationPreference={props.waitlistEntry.reservationPreference}
                     />
                  )}

                  <EncounterModeTextView
                     appointmentEncounterMode={appointmentDetails.encounterMode}
                     encounterMode={props.waitlistEntry.encounterMode}
                  />

                  <PractitionerTextView
                     appointmentPractitioner={appointmentDetails.practitioner}
                     practitioner={props.waitlistEntry.practitioner}
                     reservationPreference={props.waitlistEntry.reservationPreference}
                  />

                  <PatientTextView patient={patient} />

                  <AppointmentTimeSlotSection
                     beginDate={appointmentDetails.beginDate}
                     endDate={appointmentDetails.endDate}
                  />

                  <NoteTextView
                     note={props.waitlistEntry.note}
                     patientAvailability={props.waitlistEntry.patientAvailability}
                     reservationPreference={props.waitlistEntry.reservationPreference}
                  />

                  <ReceptionServiceTextView
                     appointmentReceptionServices={appointmentDetails.receptionServices}
                     receptionService={props.waitlistEntry.receptionService}
                  />
               </StyledFlex>
            )}
         </WaitlistEntryConfirmReservationModalLoader>

         <WaitlistEntryConfirmReservationModalActions
            entryId={props.waitlistEntry.id}
            appointmentId={props.waitlistEntry.appointmentId!}
            onSuccess={props.onClose}
         >
            {({ confirmAppointment, cancelAppointment }) => (
               <>
                  <ModalConfirmButton
                     label={translationKeys.restorePatientToWaitlist}
                     onClick={cancelAppointment}
                     variant={ButtonVariant.SECONDARY}
                     testId={'restorePatientToWaitlist'}
                  />
                  <ModalConfirmButton
                     label={translationKeys.confirmAppointment}
                     onClick={confirmAppointment}
                     testId={'confirmAppointment'}
                  />
               </>
            )}
         </WaitlistEntryConfirmReservationModalActions>

         <ModalCancelButton onClick={props.onClose} />
         <ModalCloseButton onClick={props.onClose} />
      </Modal>
   );
};
